import { Appointment } from "../types/appointment.definition";
import { Staff } from "../types/staff.definition";
import { formatMoney } from "./format.utils";

/**
 * Escapes a CSV field value by wrapping it in quotes if it contains commas, quotes, or newlines
 */
const escapeCSVField = (field: string | null | undefined): string => {
  if (field === null || field === undefined) {
    return "";
  }
  const stringValue = String(field);
  // If the field contains comma, quote, or newline, wrap it in quotes and escape internal quotes
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const DAKAR = "Africa/Dakar";

/** MED-1687: JJ/MM/AAAA, at the facility's time (Dakar), whatever the browser's time zone. */
const formatDakarDate = (value: Date): string =>
  value.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: DAKAR });

const formatDakarTime = (value: Date): string =>
  value.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: DAKAR });

const formatDakarDateTime = (value?: string): string => {
  if (!value) return "";
  const date = new Date(value);
  return `${formatDakarDate(date)} ${formatDakarTime(date)}`;
};

/**
 * Converts an array of appointments to CSV format.
 * MED-1687: headers, statuses and types follow the interface language, dates are JJ/MM/AAAA.
 */
export const appointmentsToCSV = (appointments: Appointment[], t: Translate): string => {
  const headers = [
    "id",
    "patient",
    "patientId",
    "doctor",
    "doctorId",
    "date",
    "time",
    "status",
    "type",
    "reason",
    "location",
    "bookedBy",
    "facilityId",
    "createdAt",
    "updatedAt",
  ].map((key) => escapeCSVField(t(`appointment.manage.csv.${key}`)));

  const fullName = (person?: { firstName?: string; lastName?: string } | null) =>
    person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "";

  const rows = appointments.map((appointment) => {
    const appointmentDate = appointment.appointmentTime ? new Date(appointment.appointmentTime) : null;
    return [
      escapeCSVField(appointment.id),
      escapeCSVField(fullName(appointment.patient)),
      escapeCSVField(appointment.patientId),
      escapeCSVField(fullName(appointment.doctor)),
      escapeCSVField(appointment.doctorId),
      escapeCSVField(appointmentDate ? formatDakarDate(appointmentDate) : ""),
      escapeCSVField(appointmentDate ? formatDakarTime(appointmentDate) : ""),
      escapeCSVField(
        appointment.status
          ? t(`appointment.status.${appointment.status}`, { defaultValue: appointment.status })
          : "",
      ),
      escapeCSVField(
        appointment.appointmentType
          ? t(`appointment.manage.serviceTypes.${appointment.appointmentType}`, {
              defaultValue: appointment.appointmentType,
            })
          : "",
      ),
      escapeCSVField(appointment.reason),
      escapeCSVField(appointment.location?.address || ""),
      escapeCSVField(fullName(appointment.bookedBy)),
      escapeCSVField(appointment.healthcareFacilityId?.toString()),
      escapeCSVField(formatDakarDateTime(appointment.createdAt)),
      escapeCSVField(formatDakarDateTime(appointment.updatedAt)),
    ];
  });

  return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
};

/**
 * Downloads a CSV file with the given content and filename
 */
export const downloadCSV = (content: string, filename: string): void => {
  // Create a blob with the CSV content
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });

  // Create a download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
};

/**
 * Exports appointments to a CSV file. MED-1687: a UTF-8 BOM so Excel shows the accents.
 */
export const exportAppointmentsToCSV = (
  appointments: Appointment[],
  t: Translate,
  filename?: string
): void => {
  if (appointments.length === 0) {
    return;
  }

  const csvContent = appointmentsToCSV(appointments, t);
  const defaultFilename = `${t("appointment.manage.csv.filename")}_${
    new Date().toISOString().split("T")[0]
  }.csv`;
  downloadCSV(`\uFEFF${csvContent}`, filename || defaultFilename);
};

/**
 * Converts an array of staff/doctors to CSV format
 */
export const staffToCSV = (staff: Staff[]): string => {
  // Define CSV headers
  const headers = [
    "Staff ID",
    "First Name",
    "Last Name",
    "Email",
    "Phone Number",
    "Reference Number",
    "Specialties",
    "Services",
    "Hospital/Facility",
    "Date of Birth",
    "Career Start Year",
    "Average Rating",
    "Spoken Languages",
    "Location - Address",
    "Location - City",
    "Location - State",
    "Location - Country",
    "Next Availability",
  ];

  // Create CSV rows
  const rows = staff.map((member) => {
    const specialties =
      member.specialties?.map((s) => s.label || s.code).join("; ") || "";

    const services =
      member.providedServices
        ?.map((s) => `${s.code} (${formatMoney(s.price, "en", { useGrouping: false })})`)
        .join("; ") || "";

    const spokenLanguages = member.doctorSpokenLanguages?.join("; ") || "";

    const facilityName =
      member.healthcareFacilities?.[0]?.name || member.hospitalName || "";

    const location = member.contactInfo?.location || member.location;
    const locationAddress = location?.address || "";
    const locationCity = location?.city || "";
    const locationState = location?.state || "";
    const locationCountry = location?.country || "";

    const dateOfBirth = member.dateOfBirth
      ? new Date(member.dateOfBirth).toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "";

    const careerStartYear = member.careerStartYear
      ? new Date(member.careerStartYear).getFullYear().toString()
      : "";

    const nextAvailability = member.nextAvailability
      ? new Date(member.nextAvailability).toLocaleString("en-US")
      : "";

    return [
      escapeCSVField(member.id),
      escapeCSVField(member.firstName),
      escapeCSVField(member.lastName),
      escapeCSVField(member.contactInfo?.email || member.email),
      escapeCSVField(member.contactInfo?.phoneNumber || member.phoneNumber),
      escapeCSVField(member.refNumber),
      escapeCSVField(specialties),
      escapeCSVField(services),
      escapeCSVField(facilityName),
      escapeCSVField(dateOfBirth),
      escapeCSVField(careerStartYear),
      escapeCSVField(member.averageRate?.toFixed(2)),
      escapeCSVField(spokenLanguages),
      escapeCSVField(locationAddress),
      escapeCSVField(locationCity),
      escapeCSVField(locationState),
      escapeCSVField(locationCountry),
      escapeCSVField(nextAvailability),
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  return csvContent;
};

/**
 * Exports staff/doctors to a CSV file
 */
export const exportStaffToCSV = (staff: Staff[], filename?: string): void => {
  if (staff.length === 0) {
    return;
  }

  const csvContent = staffToCSV(staff);
  const defaultFilename = `staff_report_${
    new Date().toISOString().split("T")[0]
  }.csv`;
  downloadCSV(csvContent, filename || defaultFilename);
};
